import type { DataOption } from '@/common.types'

import { Fragment, useCallback, useMemo, useState } from 'react'
import { difference, map, paths, pipe, sortBy } from 'lodash/fp'
import { ListFilterIcon, LoaderCircleIcon, SearchIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'

import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
} from '@/shared/components/ui/input-group'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Separator } from '@/shared/components/ui/separator'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { toOption } from '@/shared/tools/utils'

import { useScenarios } from './ScenariosProvider'

export default function SearchFilterField() {
	const { t } = useTranslation()

	const {
		setOptimizationGroupIds,
		setSearchText,
		searchText,
		optimizationGroupIds,
		isLoading: isScenariosLoading,
	} = useScenarios()
	const [selected, setSelected] = useState<string[]>(optimizationGroupIds)
	const [isOpen, setIsOpen] = useState(false)
	const { data: optimizationGroupsOptions, isLoading: isOptionsLoading } =
		useOptimizationGroupsQuery<DataOption[]>({
			select: pipe(
				sortBy([({ name }) => name.toLowerCase()]),
				map(paths(['id', 'name'])),
				toOption
			),
		})
	const handleApply = useCallback(() => {
		setOptimizationGroupIds(selected)
		setIsOpen(false)
	}, [selected, setOptimizationGroupIds])
	const handleOptimizationGroupSelect = useCallback((value: string) => {
		setSelected((prev) =>
			prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
		)
	}, [])
	const isApplyDisabled = useMemo(
		() =>
			selected.length !== optimizationGroupIds.length
				? false
				: difference(selected, optimizationGroupIds).length === 0,
		[selected, optimizationGroupIds]
	)
	const sortedOptions: DataOption[] = useMemo(() => {
		if (!optimizationGroupsOptions) return []
		if (!optimizationGroupIds.length) return optimizationGroupsOptions

		return pipe(
			sortBy([
				(option: DataOption) =>
					optimizationGroupIds.includes(option.value) ? 0 : 1,
			])
		)(optimizationGroupsOptions)
	}, [optimizationGroupsOptions, optimizationGroupIds])
	const isLoading = isScenariosLoading || isOptionsLoading

	return (
		<InputGroup>
			<InputGroupAddon>
				<SearchIcon />
			</InputGroupAddon>
			<InputGroupInput
				disabled={isLoading}
				placeholder={t('general_search')}
				value={searchText}
				onChange={(e) => {
					setSearchText(e.target.value)
				}}
			/>
			<InputGroupAddon align='inline-end' onClick={undefined}>
				<Popover
					open={isOpen}
					onOpenChange={(open) => {
						setIsOpen(open)

						if (!open) {
							setSelected(optimizationGroupIds)
						}
					}}
				>
					<PopoverTrigger asChild disabled={isLoading}>
						<InputGroupButton size='icon-xs' className='relative'>
							{isLoading ? (
								<LoaderCircleIcon className='size-4 animate-spin' />
							) : (
								<Fragment>
									<ListFilterIcon />
									{optimizationGroupIds.length ? (
										<span className='absolute top-0.5 right-0.5 size-2 shrink-0 rounded-full bg-primary' />
									) : null}
								</Fragment>
							)}
						</InputGroupButton>
					</PopoverTrigger>
					<PopoverContent align='start' className='space-y-1 p-0'>
						<Command className='w-96'>
							<CommandInput placeholder={t('general_search')} />
							<Virtualized asChild>
								<CommandList>
									<CommandEmpty>
										{t('no_entity_found', { entity: t('optimization_groups') })}
									</CommandEmpty>
									<CommandGroup>
										<VirtualizedVirtualizer>
											{sortedOptions?.map(({ value, label }) => {
												const isChecked = selected.includes(value)

												return (
													<CommandItem
														data-value={value}
														value={value}
														key={value}
														keywords={[label]}
														onSelect={() =>
															handleOptimizationGroupSelect(value)
														}
													>
														<Checkbox checked={isChecked} />
														<span>{label}</span>
													</CommandItem>
												)
											})}
										</VirtualizedVirtualizer>
									</CommandGroup>
								</CommandList>
							</Virtualized>
							<Separator />
							<div className='flex justify-between p-2'>
								<Button
									onClick={() => setSelected([])}
									size='sm'
									disabled={!selected.length}
								>
									{t('general_clear')}
								</Button>
								<Button
									onClick={handleApply}
									variant='primary'
									size='sm'
									disabled={isApplyDisabled}
								>
									{t('general_apply')}
								</Button>
							</div>
						</Command>
					</PopoverContent>
				</Popover>
			</InputGroupAddon>
		</InputGroup>
	)
}
